/* eslint-disable @typescript-eslint/no-explicit-any */
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { useEffect } from 'react';
import { Button, Divider, Form, Modal, Space, Table } from 'antd';
import { DefaultRouteTabs, MultipleContactModal } from '@/components/_common';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { ColumnsType } from 'antd/lib/table';
import { useDisclosure, useRouteMatchParam } from '@/hooks';
import moment from 'moment';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { useBankAccount } from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';
import {
  useBankAccountContactCreate,
  useBankAccountContactEdit,
  useBankAccountContactList,
  useBankAccountContactRemove,
  useBankAccountContactRemoveDetail,
} from '@/core/domain/enterprise/BankAccountContacts/BankAccountContacts.hooks';
import { BankAccountContact } from '@/core/domain/enterprise/BankAccountContacts/BankAccountContacts.types';
import { LegalPersonContactFormInterface } from '@/components/_common/MultipleContactModal/types';
import { PageProps } from '@/types/PageProps';
import useValidateRoutes from '@/hooks/useValidateRoutes';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { useBankAccountFormContext } from '../../BankAccountFormProvider';

const BankAccountsContactPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const [form] = Form.useForm<LegalPersonContactFormInterface>();
  const { setParamLabel } = useBreadcrumbs();
  const [modalVisible, modalActions] = useDisclosure(false, {
    onClose: () => form.resetFields(),
  });
  const bankAccountId = +useRouteMatchParam('bankAccountId');

  const { disabled } = useBankAccountFormContext();

  const bankAccount = useBankAccount(bankAccountId);
  const contactList = useBankAccountContactList(bankAccountId);
  const contactCreate = useBankAccountContactCreate();
  const contactEdit = useBankAccountContactEdit();
  const contactRemove = useBankAccountContactRemove();
  const contactRemoveDetail = useBankAccountContactRemoveDetail();

  const routeValidate = useValidateRoutes({
    route: route.parent,
    validation:
      !bankAccount.data?.bank.integration || !bankAccount.data?.companyId,
    keys: ['configurationBankSlip'],
  });

  useEffect(() => {
    if (bankAccount.data) {
      setParamLabel(':bankAccountId', bankAccount.data.bank.name);
    }
  }, [bankAccount.data]);

  useEffect(() => {
    if (contactList.data?.length && modalVisible) {
      const contactUpdated = contactList.data.find(
        (contact) => contact.id === form.getFieldValue('contactId'),
      );

      if (contactUpdated) {
        handleContactSelect(contactUpdated);
      }
    }
  }, [contactList.data, modalVisible]);

  async function handleSave() {
    try {
      const { contactId, birthDate, ...values } = await form.validateFields();

      const contactFinded = contactList.data?.find(
        (contact) => contact.id === contactId,
      );

      if (contactId && contactFinded) {
        await contactEdit.mutateAsync({
          ...values,
          bankAccountId: bankAccountId,
          id: contactId,
          birthDate: birthDate
            ? birthDate.format('YYYY-MM-DD[T00:00:00]')
            : undefined,
          detailList: values.detailList.map((detail, index) => ({
            ...detail,
            id: contactFinded.detailList[index]?.id,
            bankAccountContactId:
              contactFinded.detailList[index]?.bankAccountContactId,
          })),
        });
      } else {
        await contactCreate.mutateAsync({
          ...values,
          bankAccountId: bankAccountId,
          birthDate: birthDate
            ? birthDate.format('YYYY-MM-DD[T00:00:00]')
            : undefined,
        });

        modalActions.close();
      }
    } catch {}
  }

  function handleContactRemove(bankAccountContact: BankAccountContact) {
    Modal.confirm({
      title: 'Remover contato',
      content: `Deseja remover o contado de "${bankAccountContact?.name}"?`,
      onOk: () =>
        contactRemove.mutateAsync({
          bankAccountId: bankAccountId,
          id: bankAccountContact.id,
        }),
      okText: intl.formatMessage({ id: 'commons.form.button.remove' }),
    });
  }

  async function handleRemoveContactDetail(detailId: number) {
    await contactRemoveDetail.mutateAsync({
      bankAccountId: bankAccountId,
      detailId,
    });

    contactList.refetch();
  }

  function handleContactSelect(bankAccountContact: BankAccountContact) {
    form.setFieldsValue({
      detailList: bankAccountContact.detailList,
      contactId: bankAccountContact.id,
      birthDate: bankAccountContact?.birthDate
        ? moment(bankAccountContact.birthDate)
        : undefined,
      department: bankAccountContact.department,
      gender: bankAccountContact.gender,
      name: bankAccountContact.name,
    });
  }

  const columns: ColumnsType<BankAccountContact> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Nome',
      render: (value) => value || '-',
      width: 200,
    },
    {
      title: intl.formatMessage({ id: 'commons.contact.contactType.email' }),
      key: 'email',
      render: (_, row) =>
        row.detailList.find((cur) => cur.contactType === 'EMAIL')?.value || '-',
      width: 300,
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: 'commons.contact.contactType.phone' }),
      key: 'phone',
      render: (_, row) =>
        row.detailList.find((cur) => cur.contactType !== 'EMAIL')?.value || '-',
      width: 150,
      ellipsis: true,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      align: 'right',
      fixed: 'right',
      width: COLUMNS_WIDTH.small,
      render: (_, row: BankAccountContact) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                handleContactSelect(row);
                modalActions.open();
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleContactRemove(row)} />
        </Space>
      ),
    },
  ];

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={routeValidate}
      baseKey={String(bankAccountId)}
      basePath={`/app/financial/accounts/bankAccounts/${bankAccountId}`}
      extra={
        <Button block type="primary" onClick={modalActions.open}>
          {intl.formatMessage({ id: 'commons.entity.add' })}
        </Button>
      }
    >
      <Helmet
        title={intl.formatMessage({
          id: route.name,
        })}
      />

      <Table
        dataSource={contactList.data}
        rowKey={(row) => row.id}
        columns={columns}
        loading={contactList.isLoading}
        pagination={false}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <MultipleContactModal
        open={modalVisible}
        onCancel={modalActions.close}
        onOk={handleSave}
        confirmLoading={contactCreate.isLoading || contactEdit.isLoading}
        form={form}
        personType="NaturalPerson"
        onDetailRemove={handleRemoveContactDetail}
      />

      <Divider />

      <SpaceForButton>
        <ButtonBack
          path="/app/financial/accounts/bankAccounts"
          disabled={disabled}
        />
      </SpaceForButton>
    </DefaultRouteTabs>
  );
};

export default BankAccountsContactPage;
